<template>
    <Head :title="headerTitle || 'Results'" />
    <BaseLayout width="narrow" gap="none">
        <div v-if="loading" class="body-small text-ink-muted">Loading…</div>
        <BaseNotification v-else-if="loadError" theme="error" :text="loadError" hide-dismiss />
        <template v-else-if="cycle">
            <section class="flex flex-col gap-4">
                <a :href="backHref" class="body-small text-ink-muted hover:text-ink flex items-center gap-1 self-start">
                    <i class="bi bi-chevron-left" aria-hidden="true"></i> Back
                </a>
                <div class="flex items-start justify-between gap-4 flex-wrap">
                    <div class="min-w-0">
                        <h1 class="display-serif text-ink leading-tight">{{ headerTitle }}</h1>
                        <p v-if="reportDate" class="details text-ink-muted mt-1">{{ reportDate }}</p>
                    </div>
                    <div v-if="showResponders" class="flex items-center gap-3 shrink-0">
                        <div class="rater-cluster">
                            <BaseAvatar
                                v-for="(avatar, i) in raterAvatars"
                                :key="`ra-${i}`"
                                size="sm"
                                :initials="avatar.initial"
                                :style="{ '--base-avatar-bg': avatar.swatch.fill, '--base-avatar-text': avatar.swatch.text }" />
                        </div>
                        <BaseButton theme="tertiary" size="sm" @click="onViewResponders">View Responders</BaseButton>
                    </div>
                </div>
                <MissingSelfReflectionNotice v-if="report" :cycle="cycle" :viewer-is-subject="viewerIsSubject" />
                <div v-if="report && viewerIsSubject" class="flex items-center gap-2 flex-wrap">
                    <a
                        v-if="canRetake"
                        :href="`/reflections/360/${props.cycle_id}/self`"
                        class="base-btn-neutral base-btn-sm inline-flex items-center gap-2"
                        @click="onRetakeClick">
                        <i class="bi bi-arrow-repeat" aria-hidden="true"></i>
                        Retake
                    </a>
                    <BaseButton theme="neutral" size="sm" :disabled="downloading" class="inline-flex items-center gap-2" @click="onDownloadPdf">
                        <i class="bi bi-download" aria-hidden="true"></i>
                        {{ downloading ? "Downloading…" : "Download PDF" }}
                    </BaseButton>
                    <BaseButton theme="primary" size="sm" :disabled="startingChat" class="inline-flex items-center gap-2" @click="onChatWithNadia">
                        <i class="bi bi-stars" aria-hidden="true"></i>
                        {{ startingChat ? "Starting…" : "Chat with Nadia" }}
                        <i v-if="!startingChat" class="bi bi-arrow-right" aria-hidden="true"></i>
                    </BaseButton>
                </div>
            </section>

            <div class="mt-10 flex flex-col gap-10">
                <AwaitingSelfReflectionGate
                    v-if="!report && cycle.awaiting_self_reflection && viewerIsSubject"
                    :cycle-id="props.cycle_id"
                    :can-generate-without="cycle.can_generate_without_self_reflection ?? false"
                    :responded-count="cycle.responded_count ?? 0"
                    :total-invited="cycle.total_invited ?? 0"
                    @complete="router.visit(`/reflections/360/${props.cycle_id}/self`)"
                    @generated="onGeneratedWithoutSelf" />

                <!-- Empty state if no report yet -->
                <BaseCard v-else-if="!report" theme="muted" radius="lg" padding="lg" class="border-dashed border-border-strong text-center">
                    <p v-if="cycle.closed_without_report" class="body-small text-ink-muted">{{ t("reflections.results.closedWithoutReport") }}</p>
                    <div v-else-if="polling" class="flex flex-col items-center gap-3">
                        <LoadingSpinner class="w-5 h-5 text-ink-muted" />
                        <p class="body-small text-ink-muted">Generating your report…</p>
                    </div>
                    <p v-else class="body-small text-ink-muted">Your report is being generated. Refresh in a moment.</p>
                </BaseCard>

                <template v-else>
                    <div class="flex flex-col gap-5">
                        <p v-if="cycle.report_intro" class="body-small text-ink whitespace-pre-line">{{ cycle.report_intro }}</p>
                        <ExecutiveSummarySection v-if="report.executive_summary" :summary="report.executive_summary" :heading-level="2" />
                    </div>

                    <SuggestedGoalsSection
                        v-if="showSuggestedGoals"
                        :cycle-id="props.cycle_id"
                        :development-journeys-enabled="props.development_journeys_enabled"
                        :assessment-goal-discussion-chat-start-url="props.assessment_goal_discussion_chat_start_url"
                        :assessment-goal-adoption-url="props.assessment_goal_adoption_url"
                        :assessment-goal-dismissal-url="props.assessment_goal_dismissal_url"
                        @hidden="suggestedGoalsHidden = true" />

                    <CategoryScoresSection
                        v-if="showScores && aggregates && hasCategoryScores"
                        id="category-scores"
                        :sections="aggregates.sections ?? []"
                        :self-assess-enabled="cycle.self_assess_enabled && cycle.self_assessment_submitted"
                        :is-self-assessment-only="cycle.is_self_assessment_only"
                        :subject-name="viewerIsSubject ? '' : subjectName"
                        :max-score="defaultMaxScore" />

                    <SelfVsOthersSection
                        v-if="showScores && aggregates && hasSelfVsOthers && !cycle.is_self_assessment_only"
                        :questions="scoreQuestions"
                        :subject-name="viewerIsSubject ? '' : subjectName" />

                    <QuestionByQuestionSection
                        v-if="showScores && aggregates"
                        :questions="questionByQuestionItems"
                        :sections="aggregates.sections ?? []"
                        :subject-first-name="cycle.subject_first_name"
                        :is-self-assessment-only="cycle.is_self_assessment_only"
                        :viewer-is-subject="viewerIsSubject"
                        :anonymity-threshold="aggregates.anonymity_threshold" />

                    <ThemesSection v-if="showScores && (report.themes?.length ?? 0) > 0 && !cycle.is_self_assessment_only" :themes="report.themes" />

                    <!-- Footer CTA: chat with Nadia (subject only — the debrief chat is theirs) -->
                    <section v-if="viewerIsSubject" class="text-center pt-2">
                        <h2 class="title text-ink">Ready to make sense of it together?</h2>
                        <p class="body-small text-ink-muted mt-2 mb-6 max-w-md mx-auto">I'll help you make sense of it and turn it into a plan.</p>
                        <BaseButton theme="primary" :disabled="startingChat" class="inline-flex items-center gap-2" @click="onChatWithNadia">
                            <i class="bi bi-stars" aria-hidden="true"></i>
                            {{ startingChat ? "Starting your chat…" : "Chat with Nadia about this" }}
                            <i v-if="!startingChat" class="bi bi-arrow-right" aria-hidden="true"></i>
                        </BaseButton>
                        <BaseNotification v-if="chatError" theme="error" :text="chatError" hide-dismiss class="mt-4" />
                    </section>
                </template>
            </div>
        </template>
    </BaseLayout>
    <AssessmentDemoTools
        v-if="cycle"
        :cycle-actions="cycleDemoActions"
        :bootstrap-kind="cycle.is_self_assessment_only ? 'self' : 'sixty'"
        @deleted="router.visit('/reflections')" />
</template>

<script lang="ts">
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
export default { layout: LeftSidebarLayout };
</script>

<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3";
import { useTimeoutPoll } from "@vueuse/core";
import { fetchApi, useFetch } from "~vue/api";
import AwaitingSelfReflectionGate from "~vue/AssessmentResults/AwaitingSelfReflectionGate.vue";
import CategoryScoresSection from "~vue/AssessmentResults/CategoryScoresSection.vue";
import ExecutiveSummarySection from "~vue/AssessmentResults/ExecutiveSummarySection.vue";
import MissingSelfReflectionNotice from "~vue/AssessmentResults/MissingSelfReflectionNotice.vue";
import QuestionByQuestionSection from "~vue/AssessmentResults/QuestionByQuestionSection.vue";
import SelfVsOthersSection from "~vue/AssessmentResults/SelfVsOthersSection.vue";
import SuggestedGoalsSection from "~vue/AssessmentResults/SuggestedGoalsSection.vue";
import ThemesSection from "~vue/AssessmentResults/ThemesSection.vue";
import type { Aggregates, CycleView, PerQuestion, Report } from "~vue/AssessmentResults/types";
import BaseAvatar from "~vue/components/BaseAvatar.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseLayout from "~vue/components/BaseLayout.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import AssessmentDemoTools from "~vue/pages/reflections/AssessmentDemoTools.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { formatDate } from "~vue/utils/formatDate";
import { logUserInteraction } from "~vue/utils/logUtils";
import { getIndexedAvatarSwatch } from "~vue/utils/perspectivePalette";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{
    cycle_id: string;
    // Computed server-side from either Journeys flag so we never mount the rail
    // (and trigger a 403 + layout shift) for users who can't see suggested goals.
    show_suggested_goals: boolean;
    // Users with both flags receive the Development Journeys treatment.
    development_journeys_enabled?: boolean;
    assessment_goal_discussion_chat_start_url?: string | null;
    assessment_goal_adoption_url?: string | null;
    assessment_goal_dismissal_url?: string | null;
}>();

const cycle = ref<CycleView | null>(null);
const report = ref<Report | null>(null);
// False when a team lead is viewing a member's report: third-person copy,
// and the subject-only actions (retake, PDF, debrief chat, goals) are hidden.
const viewerIsSubject = ref(true);
const subjectName = computed(() => cycle.value?.subject_first_name ?? "");
const loading = ref(true);
const loadError = ref<string | null>(null);
// The rail can also hide itself if the API returns 403 (defensive fallback for a
// flag/JWT mismatch); combined with the server-provided flag below.
const suggestedGoalsHidden = ref(false);
const showSuggestedGoals = computed(() => props.show_suggested_goals && !suggestedGoalsHidden.value && viewerIsSubject.value);

const aggregates = computed<Aggregates | null>(() => report.value?.aggregates ?? null);
// Template-wide superset of the per-section rule in CategoryScoresSection: the
// report ends after the suggested goals.
const showScores = computed(() => !cycle.value?.hide_report_scores);
const scoreQuestions = computed<PerQuestion[]>(() => aggregates.value?.per_question.filter((q) => q.is_score) ?? []);
const questionByQuestionItems = computed<PerQuestion[]>(() => aggregates.value?.per_question.filter((q) => q.is_score || q.open_text_synthesis) ?? []);

const hasSelfVsOthers = computed(() => scoreQuestions.value.some((q) => q.self_score !== null && q.all_avg !== null));

// Self-assessment-only cycles redirect their detail page straight here, so the
// back link returns to the hub instead of bouncing through that redirect. 360
// cycles keep the cycle-detail page (rater progress) as their back target.
const backHref = computed(() => (cycle.value?.is_self_assessment_only ? "/reflections?tab=self" : `/reflections?tab=sixty`));

const headerTitle = computed(() => {
    if (!cycle.value) {
        return "";
    }
    const title = cycle.value.template.name ? `${cycle.value.template.name} Results` : "Results";
    return viewerIsSubject.value ? title : `${subjectName.value}'s ${title}`;
});

const reportDate = computed(() => {
    const iso = report.value?.generated_at ?? cycle.value?.unlocked_at ?? null;
    if (!iso) {
        return "";
    }
    return formatDate(iso, "date-long");
});

const RATER_AVATAR_MAX = 6;
const raterAvatars = computed(() => {
    // We don't leak rater identities in the report payload for anonymity, so
    // synthesize a fixed set of initial-shaped placeholders proportional to the
    // rater count. Just visual chrome for the header cluster.
    const roles = aggregates.value?.rater_breakdown ?? {};
    const total = Object.values(roles).reduce((a, b) => a + b, 0);
    return Array.from({ length: Math.min(total, RATER_AVATAR_MAX) }, (_v, i) => ({ initial: String.fromCharCode(65 + i), swatch: getIndexedAvatarSwatch(i) }));
});

// "View Responders" scrolls to the per-relationship breakdown, which is still rater
// status: a subject who invited one manager reads "manager: 1" as that manager having
// responded. An organization that anonymizes rater status drops the cluster entirely.
const showResponders = computed(() => cycle.value !== null && !cycle.value.is_self_assessment_only && !cycle.value.rater_status_hidden && raterAvatars.value.length > 0);

function onViewResponders() {
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_RESULTS_VIEWED, {
        cycle_id: props.cycle_id,
        action: "view_responders_toggle",
    });
    // Simple UX: scroll to the rater-breakdown call-out below.
    // (Deferred to a dedicated responders modal in a follow-up.)
    const el = document.getElementById("category-scores");
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
}

// Subjects can redo a self-assessment while it's still open (UNLOCKED). The
// backend re-aggregates the report on resubmit; CLOSED cycles can't be edited.
// Only the subject's own cycle: the demo endpoints act on the caller's cycles. This payload
// carries no pending-rater count, so a fill with nobody left to answer reports that inline.
const cycleDemoActions = computed(() => {
    const currentCycle = cycle.value;
    if (currentCycle === null || !viewerIsSubject.value) {
        return [];
    }
    return [{ id: currentCycle.id, label: currentCycle.template.name, canFill: currentCycle.state === "collecting" }];
});
const canRetake = computed(() => viewerIsSubject.value && cycle.value?.is_self_assessment_only === true && cycle.value?.state === "unlocked");

const hasCategoryScores = computed(() => (aggregates.value?.sections ?? []).some((s) => s.avg !== null));

const defaultMaxScore = computed(() => {
    const first = scoreQuestions.value[0];
    return first?.max_score ?? first?.distribution.length ?? 5;
});

const startingChat = ref(false);
const chatError = ref<string | null>(null);
const downloading = ref(false);
const { t } = useI18n();

// --- Polling for report generation ---
let pollStartedAt = 0;
const POLL_TIMEOUT_MS = 60_000;

const {
    resume: resumePoll,
    pause: stopPolling,
    isActive: polling,
} = useTimeoutPoll(
    async () => {
        if (Date.now() - pollStartedAt > POLL_TIMEOUT_MS) {
            stopPolling();
            return;
        }
        const { data } = await useFetch(`/v1/reflections/cycles/${props.cycle_id}/results`).json();
        const payload = data.value as { cycle: CycleView; report: Report | null; viewer_is_subject?: boolean } | null;
        if (payload?.report) {
            cycle.value = payload.cycle;
            report.value = payload.report;
            viewerIsSubject.value = payload.viewer_is_subject ?? true;
            stopPolling();
        }
    },
    3000,
    { immediate: false },
);

function startPolling() {
    pollStartedAt = Date.now();
    resumePoll();
}

// The report is queued from the raters alone; drop the gate and poll for it.
function onGeneratedWithoutSelf() {
    if (cycle.value) {
        cycle.value = { ...cycle.value, awaiting_self_reflection: false, can_generate_without_self_reflection: false };
    }
    startPolling();
}

function onRetakeClick() {
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_RESULTS_RETAKE_CLICKED, {
        cycle_id: props.cycle_id,
    });
}

async function onDownloadPdf() {
    if (downloading.value) {
        return;
    }
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_RESULTS_PDF_DOWNLOAD_CLICKED, {
        cycle_id: props.cycle_id,
    });
    downloading.value = true;
    try {
        const response = await fetchApi(`/v1/reflections/cycles/${props.cycle_id}/export-pdf`);
        if (!response.ok) {
            return;
        }
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const disposition = response.headers.get("Content-Disposition");
        const match = disposition?.match(/filename="?([^"]+)"?/);
        a.download = match?.[1] ?? (cycle.value?.is_self_assessment_only ? "self_assessment_report.pdf" : "360_report.pdf");
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    } finally {
        downloading.value = false;
    }
}

async function onChatWithNadia() {
    if (startingChat.value) {
        return;
    }
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_RESULTS_CHAT_WITH_NADIA_CLICKED, {
        cycle_id: props.cycle_id,
    });
    startingChat.value = true;
    chatError.value = null;
    try {
        const { data, error: fetchError } = await useFetch(`/v1/reflections/cycles/${props.cycle_id}/debrief-chat`).post().json();
        if (fetchError.value) {
            chatError.value = fetchError.value?.message || "Could not start the chat. Please try again.";
            return;
        }
        const chatUrl = (data.value as { chat_url: string }).chat_url;
        if (chatUrl) {
            router.visit(chatUrl);
        }
    } finally {
        startingChat.value = false;
    }
}

onMounted(async () => {
    const { data, error: fetchError, response } = await useFetch(`/v1/reflections/cycles/${props.cycle_id}/results`).json();
    loading.value = false;
    if (fetchError.value) {
        // 400 = cycle not yet unlocked → bounce to cycle detail.
        if (response.value?.status === 400) {
            router.visit(`/reflections/360/${props.cycle_id}`);
            return;
        }
        loadError.value = fetchError.value?.message || "Could not load results.";
        return;
    }
    const payload = data.value as { cycle: CycleView; report: Report | null; viewer_is_subject?: boolean };
    cycle.value = payload.cycle;
    report.value = payload.report;
    viewerIsSubject.value = payload.viewer_is_subject ?? true;
    const ratersByRole = payload.report?.aggregates?.rater_breakdown ?? {};
    const raterCount = Object.values(ratersByRole).reduce((a, b) => a + b, 0);
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_RESULTS_VIEWED, {
        cycle_id: props.cycle_id,
        report_ready: payload.report !== null,
        rater_count: raterCount,
        has_self_assess: payload.cycle.self_assess_enabled,
    });

    if (!payload.report && !payload.cycle.awaiting_self_reflection && !payload.cycle.closed_without_report) {
        startPolling();
    }
});
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .rater-cluster {
        @apply flex -space-x-2;
    }
}
</style>
